import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Building2, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCompanies, useSoftDeleteCompany } from "@/hooks/useCompanies";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

function CompanyLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: companies, isLoading } = useCompanies();
  const softDelete = useSoftDeleteCompany();

  const filteredCompanies =
    companies?.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const handleDelete = () => {
    if (deleteId) {
      softDelete.mutate(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Companies">
        <Button offlineEdit onClick={() => navigate("/companies/new")}>
          <Plus className="h-4 w-4" />
          New Company
        </Button>
      </PageHeader>

      <div className="w-full sm:w-80">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search companies..."
        />
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Types</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Loading companies...
                </TableCell>
              </TableRow>
            ) : filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Building2 className="h-8 w-8" />
                    <p>
                      {search
                        ? "No companies match your search."
                        : "No companies yet. Create your first one!"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map((company) => (
                <TableRow
                  key={company.id}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <CompanyLinkCell
                    to={`/companies/${company.id}`}
                    className="font-mono text-sm"
                  >
                    {company.number || "-"}
                  </CompanyLinkCell>
                  <CompanyLinkCell
                    to={`/companies/${company.id}`}
                    className="font-medium"
                  >
                    {company.name}
                  </CompanyLinkCell>
                  <CompanyLinkCell
                    to={`/companies/${company.id}`}
                    className="max-w-[250px] truncate"
                  >
                    {company.invoice_address || "-"}
                  </CompanyLinkCell>
                  <CompanyLinkCell to={`/companies/${company.id}`}>
                    <div className="flex flex-wrap gap-1">
                      {company.types?.map((type) => (
                        <Badge
                          key={type}
                          variant="secondary"
                          className="text-xs capitalize"
                        >
                          {type.replace("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </CompanyLinkCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem offlineEdit asChild>
                          <Link to={`/companies/${company.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          offlineEdit
                          className="text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(company.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move the company to trash. You can restore it later if
              needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
